import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../layout/Layout";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  CircularProgress,
  Alert,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SettingsIcon from "@mui/icons-material/Settings";

import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
} from "../../services/apiCalls";
import { NotificationContext } from "../../components/Notifications/NotificationContext";
import NotificationPreferencesModal from "../../components/Notifications/NotificationPreferencesModal";

const severityConfig = {
  CRITICAL: { color: "error", icon: <ErrorIcon />, bgColor: "#ffebee" },
  WARNING: { color: "warning", icon: <WarningAmberIcon />, bgColor: "#fff3e0" },
  INFO: { color: "info", icon: <InfoIcon />, bgColor: "#e3f2fd" },
  SUCCESS: { color: "success", icon: <CheckCircleIcon />, bgColor: "#e8f5e9" },
};

const categoryColors = {
  RISK: "#d32f2f",
  RECOMMENDATIONS: "#1976d2",
  QUESTIONNAIRES: "#388e3c",
  ADMINISTRATIVE: "#7b1fa2",
};

// Map backend NotificationType enum values to i18n translation keys
const typeToTranslationKey = {
  "Risk Level Increased": "riskLevelIncreased",
  "High Risk Patient Enrolled": "highRiskPatientEnrolled",
  "Pending Recommendation": "pendingRecommendation",
  "New Questionnaire Response": "newQuestionnaireResponse",
  "New Patient Assigned": "newPatientAssigned",
  "Recommendation Accepted": "recommendationAccepted",
  "Recommendation Rejected": "recommendationRejected",
  "Recommendation Modified": "recommendationModified",
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const { refreshCount } = useContext(NotificationContext);
  const { t } = useTranslation("notifications");
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await getNotifications();
      // Normalize data - handle both wrapped {NotificationModel: {...}} and direct {...} formats
      const normalized = data.map((item) =>
        item.NotificationModel ? item.NotificationModel : item
      );
      setNotifications(normalized);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const getSeverityValue = (severity) => {
    if (!severity) return "INFO";
    const value =
      typeof severity === "object" ? severity.value || "INFO" : severity;
    return value.toUpperCase();
  };

  const getCategoryValue = (category) => {
    if (!category) return "ADMINISTRATIVE";
    const value =
      typeof category === "object"
        ? category.value || "ADMINISTRATIVE"
        : category;
    return value.toUpperCase();
  };

  const getTypeValue = (type) => {
    if (!type) return "";
    return typeof type === "object" ? type.value || "" : type;
  };

  const getTranslatedTitle = (notification) => {
    const typeValue = getTypeValue(notification.type);
    const key = typeToTranslationKey[typeValue];
    if (key) {
      return t(`types.${key}`);
    }
    return notification.title;
  };

  const getTranslatedMessage = (notification) => {
    const typeValue = getTypeValue(notification.type);
    const key = typeToTranslationKey[typeValue];
    const extra = notification.extra_data || {};
    if (key && extra.patient_name) {
      const params = {
        patientName: extra.patient_name,
        oldRisk: extra.old_risk,
        newRisk: extra.new_risk,
        riskLevel: extra.risk_level,
        daysPending: extra.days_pending,
        questionnaireType: extra.questionnaire_type,
      };
      return t(`messages.${key}`, params);
    }
    return notification.message;
  };

  const filteredNotifications = notifications.filter((n) => {
    if (categoryFilter && getCategoryValue(n.category) !== categoryFilter)
      return false;
    if (showUnreadOnly && n.is_read) return false;
    return true;
  });

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      refreshCount();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );
      refreshCount();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      setNotifications((prev) =>
        prev.filter((n) => n.id !== notificationId)
      );
      refreshCount();
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }

    // Navigate to patient if user_uuid is present
    if (notification.user_uuid) {
      navigate("/citizens");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h4">{t("title")}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
              size="small"
            >
              {t("markAllRead")}
            </Button>
            <IconButton onClick={() => setPreferencesOpen(true)}>
              <SettingsIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t("filterCategory")}</InputLabel>
            <Select
              value={categoryFilter}
              label={t("filterCategory")}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">{t("allCategories")}</MenuItem>
              <MenuItem value="RISK">{t("categories.risk")}</MenuItem>
              <MenuItem value="RECOMMENDATIONS">
                {t("categories.recommendations")}
              </MenuItem>
              <MenuItem value="QUESTIONNAIRES">
                {t("categories.questionnaires")}
              </MenuItem>
              <MenuItem value="ADMINISTRATIVE">
                {t("categories.administrative")}
              </MenuItem>
            </Select>
          </FormControl>

          <Button
            variant={showUnreadOnly ? "contained" : "outlined"}
            onClick={() => setShowUnreadOnly(!showUnreadOnly)}
            size="small"
          >
            {t("unreadOnly")}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Notifications List */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Alert severity="info">{t("noNotifications")}</Alert>
        ) : (
          <Stack spacing={2}>
            {filteredNotifications.map((notification) => {
              if (!notification) return null;

              const severityValue = getSeverityValue(notification.severity);
              const categoryValue = getCategoryValue(notification.category);
              const typeValue = getTypeValue(notification.type);
              const config = severityConfig[severityValue] || severityConfig.INFO;

              return (
                <Card
                  key={notification.id}
                  sx={{
                    bgcolor: notification.is_read ? "inherit" : config.bgColor,
                    opacity: notification.is_read ? 0.8 : 1,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 3,
                    },
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 2, flex: 1 }}>
                        <Box sx={{ color: `${config.color}.main`, mt: 0.5 }}>
                          {config.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {getTranslatedTitle(notification)}
                            </Typography>
                            {!notification.is_read && (
                              <Chip
                                label={t("new")}
                                size="small"
                                color="primary"
                              />
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {getTranslatedMessage(notification)}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={t(`categories.${categoryValue.toLowerCase()}`)}
                              size="small"
                              sx={{
                                bgcolor: categoryColors[categoryValue],
                                color: "white",
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(notification.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notification.id);
                        }}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Box>

      <NotificationPreferencesModal
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </Layout>
  );
};

export default Notifications;
